import type { MemoizedSelector, Selector } from '@ngrx/store';
import { createSelector } from '@ngrx/store';

import { getMetaAdapter, getMetaSelectors } from '../meta-adapter';
import type { Reference } from '../types';
import { createSelectorWithReferences } from '../utils';
import { getSingleEntityActions } from './actions';
import { getSingleEntityDataAdapter } from './data';
import type { NotNull, SingleEntityAdapter, SingleEntityState } from './types';

const singleEntityInitialState: SingleEntityState<any> = Object.freeze(<const>{
  data: null,
  loaded: false,
  pending: false,
  failed: false,
  error: null,
  expiresAt: null,
});

export const createSingleEntityAdapter = <Entity extends object | null>(
  baseSelector: Selector<any, SingleEntityState<Entity>>,
  namespace: string,
  references: Array<Reference<NotNull<Entity>>> = [],
  ttlInMs?: number,
) => {
  const adapter: SingleEntityAdapter<Entity> = {
    ...getMetaAdapter<SingleEntityState<Entity>>(),
    ...getSingleEntityDataAdapter(singleEntityInitialState),
  };

  const defaultActionsConfig = getSingleEntityActions<Entity>(adapter, ttlInMs);

  const dataSelector = createSelector<any, any, Entity | null>(baseSelector, state => state?.data);

  const ttlValidatedDataSelector = ttlInMs
    ? createSelector(
        getMetaSelectors(baseSelector, namespace).expiresAt,
        dataSelector,
        () => Date.now(),
        (expiresAt, data, now) => {
          const isExpired = expiresAt ? now > expiresAt : false;
          return isExpired ? null : data;
        },
      )
    : dataSelector;

  const dataWithRefsSelector = <MemoizedSelector<any, Entity | null>>(
    createSelectorWithReferences(ttlValidatedDataSelector, references, data => data)
  );

  const selectors = {
    ...getMetaSelectors(baseSelector, namespace),
    data: references.length ? dataWithRefsSelector : ttlValidatedDataSelector,
    state: createSelector(baseSelector, (state: SingleEntityState<Entity>) => state),
  };

  return {
    adapter,
    selectors,
    defaultActionsConfig,
    initialState: <SingleEntityState<Entity>>singleEntityInitialState,
  };
};
